import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PhotoPlaceholder from "@/components/PhotoPlaceholder/PhotoPlaceholder";
import PropertyResponse from "@/model/PropertyResponse";
import { formatMoney } from "@/utils/pricing";
import "@/components/PropertyCard/PropertyCard.scss";

interface PropertyCardProps {
  property: PropertyResponse;
  /** Query string (without "?") carried over to the details page, e.g. the searched dates. */
  linkSearch?: string;
  /** Number of nights searched; when > 0 the card also shows the stay total. */
  nights?: number;
  highlighted?: boolean;
  onHoverChange?: (propertyId: string | null) => void;
}

function PropertyCard({
  property,
  linkSearch,
  nights = 0,
  highlighted = false,
  onHoverChange,
}: PropertyCardProps) {
  const { t, i18n } = useTranslation();
  const cover = property.imageUrls?.[0];
  const to = `/properties/${property.id}${linkSearch ? `?${linkSearch}` : ""}`;
  const stayTotal =
    nights > 0
      ? (property.nightlyPriceAmount ?? 0) * nights +
        (property.cleaningFeeAmount ?? 0)
      : null;

  return (
    <Link
      to={to}
      className={`property-card${highlighted ? " property-card--highlighted" : ""}`}
      onMouseEnter={() => onHoverChange?.(property.id ?? null)}
      onMouseLeave={() => onHoverChange?.(null)}
    >
      <div className="property-card__image">
        {cover ? (
          <img src={cover} alt={property.title ?? ""} loading="lazy" />
        ) : (
          <PhotoPlaceholder />
        )}
        {property.propertyType && (
          <span className="property-card__badge">
            {t(`property.type.${property.propertyType}`)}
          </span>
        )}
      </div>

      <div className="property-card__body">
        <p className="property-card__location">
          {[property.address?.city, property.address?.country]
            .filter(Boolean)
            .join(", ")}
        </p>
        <div className="property-card__title-row">
          <h3>{property.title}</h3>
          {property.reviewCount && property.ratingAverage !== undefined ? (
            <span
              className="property-card__rating"
              aria-label={t("reviews.cardRating", {
                rating: property.ratingAverage.toFixed(1),
                count: property.reviewCount,
              })}
            >
              ★ {property.ratingAverage.toFixed(1)}
              <span aria-hidden="true"> ({property.reviewCount})</span>
            </span>
          ) : (
            <span className="property-card__rating property-card__rating--new">
              {t("reviews.new")}
            </span>
          )}
        </div>
        <p className="property-card__capacity">
          {t("property.capacity", {
            guests: property.maxGuests ?? 0,
            bedrooms: property.bedrooms ?? 0,
            beds: property.beds ?? 0,
          })}
        </p>
        <p className="property-card__price">
          <strong>
            {formatMoney(
              property.nightlyPriceAmount,
              property.priceCurrency,
              i18n.language
            )}
          </strong>{" "}
          <span>{t("property.perNight")}</span>
          {stayTotal !== null && (
            <span className="property-card__total">
              {t("property.stayTotal", {
                total: formatMoney(
                  stayTotal,
                  property.priceCurrency,
                  i18n.language
                ),
                count: nights,
              })}
            </span>
          )}
        </p>
      </div>
    </Link>
  );
}

export default PropertyCard;
export type { PropertyCardProps };
